import { palette, Box, Icons, Tooltip } from '@munifocus/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The stand-in for a download link when the document has no file behind it yet
 * (#418). A form record can exist before its artefact does, and the old markup
 * rendered a real anchor with `href={undefined}` — it kept the Download icon,
 * the hover underline and the timestamp, so it read as an active link and did
 * nothing when clicked.
 *
 * This renders an explicitly inert control instead: muted, `not-allowed`
 * cursor, `aria-disabled`, and a tooltip explaining why. It stays focusable
 * (`tabIndex=0`) so keyboard and screen-reader users reach the same explanation
 * a mouse user gets from the tooltip — a disabled control that can't be focused
 * announces nothing at all.
 */
export function UnavailableDownload({
  children,
  accessibleLabel,
  iconSize = 14,
  gap = 0.5,
}: {
  /** The visible row content (a caption or the document name). */
  children: ReactNode;
  /**
   * Overrides the accessible name. Pass a format-specific label when several
   * unavailable rows sit together (e.g. Word + PDF), so assistive tech
   * announces them distinctly rather than repeating one generic hint.
   */
  accessibleLabel?: string;
  iconSize?: number;
  gap?: number;
}) {
  const { t } = useTranslation();
  const hint = t('gov.forms.notGeneratedHint');

  return (
    <Tooltip title={hint}>
      <Box
        component="span"
        role="note"
        tabIndex={0}
        aria-disabled="true"
        aria-label={accessibleLabel ?? hint}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap,
          color: palette.text.muted,
          cursor: 'not-allowed',
        }}
      >
        <Icons.Download size={iconSize} aria-hidden="true" />
        {children}
      </Box>
    </Tooltip>
  );
}
